import { evaluateOperatorReadiness } from '@repo/types'
import { useMemo } from 'react'
import { trpc } from '@/lib/trpc'
import { deriveAppLaunchState } from './launch-state'
import type {
  AvailabilitySlotRecord,
  DayLocationDefaultRecord,
  OperatorConfigurationState,
  OperatorLocationRecord,
  OperatorProfile,
  ScheduleBreakRecord,
  ScheduleOverrideRecord,
  ScheduleRecord,
  ServiceAreaRecord,
  ServiceRecord,
} from './types'

function isCalendarSyncRequiredForLaunch() {
  return import.meta.env.VITE_CALENDAR_SYNC_REQUIRED_FOR_LAUNCH !== 'false'
}

function normalizeServiceAreaPolicy(row: unknown): ServiceAreaRecord {
  const policy = row as {
    id: string
    operatorId: string
    name?: string | null
    isDefault?: boolean | null
    centerAddressText?: string | null
    centerLat?: string | null
    centerLng?: string | null
    centerH3Index?: string | null
    hardRadiusMiles: number
    softRadiusMiles: number
    includedPostalCodesJson?: string | null
    excludedPostalCodesJson?: string | null
    coverageSource?: string | null
    coverageUpdatedAt?: string | Date | null
    surchargeType?: 'flat' | 'percent' | 'per_mile' | null
    surchargeAmount?: number | null
    maxDriveTimeBetweenMinutes?: number | null
  }

  return {
    id: policy.id,
    operatorId: policy.operatorId,
    name: policy.name ?? 'Booking area policy',
    isDefault: Boolean(policy.isDefault),
    centerAddressText: policy.centerAddressText ?? null,
    centerLat: policy.centerLat ?? null,
    centerLng: policy.centerLng ?? null,
    centerH3Index: policy.centerH3Index ?? null,
    hardRadiusMiles: policy.hardRadiusMiles,
    softRadiusMiles: policy.softRadiusMiles,
    includedPostalCodesJson: policy.includedPostalCodesJson ?? null,
    excludedPostalCodesJson: policy.excludedPostalCodesJson ?? null,
    coverageSource: policy.coverageSource ?? null,
    coverageUpdatedAt: policy.coverageUpdatedAt ?? null,
    surchargeType: policy.surchargeType ?? null,
    surchargeAmount: policy.surchargeAmount ?? null,
    maxDriveTimeBetweenMinutes: policy.maxDriveTimeBetweenMinutes ?? null,
  } satisfies ServiceAreaRecord
}

export function useOperatorConfiguration() {
  const operatorQuery = trpc.operator.get.useQuery()
  const hasOperator = Boolean(operatorQuery.data)

  const servicesQuery = trpc.services.list.useQuery(undefined, { enabled: hasOperator })
  const scheduleQuery = trpc.schedule.get.useQuery(undefined, { enabled: hasOperator })
  const locationsQuery = trpc.locations.list.useQuery(undefined, { enabled: hasOperator })
  const calendarConnectionsQuery = trpc.calendar.connections.list.useQuery(undefined, {
    enabled: hasOperator,
  })
  const serviceAreaPoliciesQuery = trpc.serviceArea.list.useQuery(undefined, {
    enabled: hasOperator,
  })

  const scheduleId = scheduleQuery.data?.schedule?.id
  const overridesQuery = trpc.schedule.overrides.list.useQuery(
    { scheduleId: scheduleId ?? '' },
    { enabled: Boolean(scheduleId) }
  )

  const services = (servicesQuery.data ?? []) as ServiceRecord[]

  const configuration = useMemo<OperatorConfigurationState>(() => {
    const serviceAreaPolicies = ((serviceAreaPoliciesQuery.data ?? []) as unknown[]).map(
      normalizeServiceAreaPolicy
    )
    const defaultServiceArea =
      serviceAreaPolicies.find((policy) => policy.isDefault) ?? serviceAreaPolicies[0] ?? null

    const rawLocations = ((locationsQuery.data?.locations ?? []) as unknown[]).map((row) => {
      const location = row as {
        id: string
        label: string
        addressText: string | null
        lat: string | null
        lng: string | null
        h3Index: string | null
        isServiceBase?: boolean | null
        hardRadiusMiles?: number | null
        softRadiusMiles?: number | null
        surchargeType?: 'flat' | 'percent' | 'per_mile' | null
        surchargeAmount?: number | null
        maxDriveTimeBetweenMinutes?: number | null
        isDefaultStart: boolean
        isDefaultEnd: boolean
      }

      return {
        id: location.id,
        label: location.label,
        addressText: location.addressText,
        lat: location.lat,
        lng: location.lng,
        h3Index: location.h3Index,
        isServiceBase: Boolean(location.isServiceBase),
        hardRadiusMiles: location.hardRadiusMiles ?? null,
        softRadiusMiles: location.softRadiusMiles ?? null,
        surchargeType: location.surchargeType ?? null,
        surchargeAmount: location.surchargeAmount ?? null,
        maxDriveTimeBetweenMinutes: location.maxDriveTimeBetweenMinutes ?? null,
        isDefaultStart: location.isDefaultStart,
        isDefaultEnd: location.isDefaultEnd,
      } satisfies OperatorLocationRecord
    })

    return {
      operator: operatorQuery.data
        ? {
            id: operatorQuery.data.id,
            businessName: operatorQuery.data.businessName,
            slug: operatorQuery.data.slug,
            bio: operatorQuery.data.bio ?? null,
            phone: operatorQuery.data.phone ?? null,
            timezone: operatorQuery.data.timezone,
            logoUrl: operatorQuery.data.logoUrl ?? null,
            isActive: Boolean(operatorQuery.data.isActive),
            artifactPublicationState: (operatorQuery.data.artifactPublicationState ??
              'draft') as OperatorProfile['artifactPublicationState'],
            launchState: (operatorQuery.data.launchState ??
              'onboarding_required') as OperatorProfile['launchState'],
            selectedBillingModel: (operatorQuery.data.selectedBillingModel ??
              null) as OperatorProfile['selectedBillingModel'],
            expectedMonthlyBookings: operatorQuery.data.expectedMonthlyBookings ?? null,
            bookingPageTheme: operatorQuery.data.bookingPageTheme ?? null,
            goLiveRequestedAt: operatorQuery.data.goLiveRequestedAt ?? null,
            wentLiveAt: operatorQuery.data.wentLiveAt ?? null,
          }
        : null,
      schedule: ((scheduleQuery.data?.schedule ?? null) as ScheduleRecord | null)
        ? {
            ...(scheduleQuery.data?.schedule as ScheduleRecord),
            serviceAreaId: scheduleQuery.data?.schedule?.serviceAreaId ?? null,
            isDefault: Boolean(scheduleQuery.data?.schedule?.isDefault),
          }
        : null,
      slots: ((scheduleQuery.data?.slots ?? []) as unknown[]).map((row) => {
        const slot = row as {
          id: string
          dayOfWeek: number
          startTime: string
          endTime: string
          locationId?: string | null
        }

        return {
          id: slot.id,
          dayOfWeek: slot.dayOfWeek,
          startTime: slot.startTime,
          endTime: slot.endTime,
          locationId: slot.locationId ?? null,
        } satisfies AvailabilitySlotRecord
      }),
      breaks: (scheduleQuery.data?.breaks ?? []) as ScheduleBreakRecord[],
      overrides: ((overridesQuery.data ?? []) as unknown[]).map((row) => {
        const override = row as {
          id: string
          date: string
          isAvailable: boolean
          startTime: string | null
          endTime: string | null
        }

        return {
          id: override.id,
          date: override.date,
          isAvailable: override.isAvailable,
          startTime: override.startTime,
          endTime: override.endTime,
        } satisfies ScheduleOverrideRecord
      }),
      services,
      locations: rawLocations.sort((left, right) => left.label.localeCompare(right.label)),
      serviceAreaPolicies,
      serviceArea: defaultServiceArea,
      calendarConnections: ((calendarConnectionsQuery.data ?? []) as unknown[]).map((row) => {
        const connection = row as {
          status: string
          syncError: string | null
          sources: Array<{
            importEnabled: boolean
            writeDestination: boolean
          }>
        }

        return {
          status: connection.status,
          syncError: connection.syncError,
          sources: connection.sources.map((source) => ({
            importEnabled: source.importEnabled,
            writeDestination: source.writeDestination,
          })),
        }
      }),
      dayDefaults: ((locationsQuery.data?.dayDefaults ?? []) as unknown[]).map((row) => {
        const item = row as {
          id: string
          dayOfWeek: number | null
          date?: string | null
          startLocationId: string | null
          endLocationId: string | null
        }

        return {
          id: item.id,
          dayOfWeek: item.dayOfWeek ?? null,
          date: item.date ?? null,
          startLocationId: item.startLocationId,
          endLocationId: item.endLocationId,
        } satisfies DayLocationDefaultRecord
      }),
    }
  }, [
    calendarConnectionsQuery.data,
    locationsQuery.data?.dayDefaults,
    locationsQuery.data?.locations,
    operatorQuery.data,
    overridesQuery.data,
    scheduleQuery.data?.breaks,
    scheduleQuery.data?.schedule,
    scheduleQuery.data?.slots,
    serviceAreaPoliciesQuery.data,
    services,
  ])

  const readiness = useMemo(
    () =>
      evaluateOperatorReadiness({
        operator: configuration.operator
          ? {
              businessName: configuration.operator.businessName,
              slug: configuration.operator.slug,
              phone: configuration.operator.phone,
            }
          : null,
        services: configuration.services.map((service) => ({
          isActive: service.isActive,
          durationMinutes: service.durationMinutes,
          price: service.price,
        })),
        serviceArea: configuration.serviceArea
          ? {
              centerLat: configuration.serviceArea.centerLat,
              centerLng: configuration.serviceArea.centerLng,
              softRadiusMiles: configuration.serviceArea.softRadiusMiles,
              hardRadiusMiles: configuration.serviceArea.hardRadiusMiles,
            }
          : null,
        availabilitySlots: configuration.slots.map((slot) => ({ id: slot.id })),
        calendarSyncRequired: isCalendarSyncRequiredForLaunch(),
        calendarConnections: configuration.calendarConnections,
      }),
    [configuration]
  )
  const onboardingComplete = readiness.isLaunchReady
  const launchState = useMemo(
    () =>
      deriveAppLaunchState({
        onboardingComplete,
        storedLaunchState: configuration.operator?.launchState ?? null,
      }),
    [configuration.operator?.launchState, onboardingComplete]
  )

  const isLoading =
    operatorQuery.isLoading ||
    servicesQuery.isLoading ||
    scheduleQuery.isLoading ||
    locationsQuery.isLoading ||
    calendarConnectionsQuery.isLoading ||
    serviceAreaPoliciesQuery.isLoading ||
    overridesQuery.isLoading

  const hasError =
    operatorQuery.error ||
    servicesQuery.error ||
    scheduleQuery.error ||
    locationsQuery.error ||
    calendarConnectionsQuery.error ||
    serviceAreaPoliciesQuery.error ||
    overridesQuery.error ||
    null

  async function refreshAll() {
    const operatorResult = await operatorQuery.refetch()
    if (!operatorResult.data) {
      return
    }

    await Promise.all([
      servicesQuery.refetch(),
      scheduleQuery.refetch(),
      locationsQuery.refetch(),
      calendarConnectionsQuery.refetch(),
      serviceAreaPoliciesQuery.refetch(),
    ])

    if (scheduleId) {
      await overridesQuery.refetch()
    }
  }

  return {
    configuration,
    readiness,
    onboardingComplete,
    launchState,
    isLoading,
    error: hasError,
    refreshAll,
  }
}
